<template>
    <aside id="Sidebar" class="bg-primary p-0 flex-column position-relative">

        <!-- Logo -->
        <div class="sidebar-logo"
             @click.stop="router.push({ name: session.rol_name === 'administrator' ? 'dashboard' : 'experiences'})">
            <img :src="logo" class="img-fluid" alt="farrther">
        </div>

        <!-- Nav Items -->
        <nav class="sidebar-nav">

            <template v-for="(item, index) in menu" :key="`menu-${index}`">
                <template v-if="item.permited_roles.includes(session.rol_name)">

                    <!-- No submenu -->
                    <button v-if="!item.hasOwnProperty('submenu')"
                            @click="goToOption(index, item.route)"
                            class="nav-btn"
                            :class="{ 'nav-btn--active': menu_active === item.label }"
                            type="button">
                        <img :src="item.icon" class="nav-icon" :alt="item.label">
                        <span class="nav-label">{{ item.label }}</span>
                    </button>

                    <!-- With submenu -->
                    <div v-else class="nav-group">
                        <button @click="toggleMenu(index)"
                                class="nav-btn"
                                :class="{
                                    'nav-btn--active': menu_active === item.label,
                                    'nav-btn--open':   item.open
                                }"
                                type="button">
                            <img :src="item.icon" class="nav-icon" :alt="item.label">
                            <span class="nav-label">{{ item.label }}</span>
                            <span class="nav-chevron" :class="{ 'nav-chevron--open': item.open }">›</span>
                        </button>

                        <transition name="submenu">
                            <div v-if="item.open" class="submenu">
                                <template v-for="(sub, si) in item.submenu" :key="`sub-${si}`">
                                    <button v-if="sub.permited_roles.includes(session.rol_name)"
                                            @click="() => { router.push({ name: sub.route, params: { type: sub.params } }); store.commit('setSearch', '') }"
                                            class="sub-btn"
                                            :class="{ 'sub-btn--active': current_router_name === sub.route }"
                                            type="button">
                                        {{ sub.label }}
                                    </button>
                                </template>
                            </div>
                        </transition>
                    </div>

                </template>
            </template>

            <!-- Divider before Settings -->
            <div class="sidebar-divider" v-if="session.rol_name === 'administrator'"></div>

            <!-- Settings accordion -->
            <div class="nav-group" v-if="session.rol_name === 'administrator'">
                <button @click="open_menu = !open_menu"
                        class="nav-btn"
                        :class="{ 'nav-btn--open': open_menu }"
                        type="button">
                    <img :src="gear" class="nav-icon" alt="settings">
                    <span class="nav-label">Settings</span>
                    <span class="nav-chevron" :class="{ 'nav-chevron--open': open_menu }">›</span>
                </button>

                <transition name="submenu">
                    <div v-if="open_menu" class="submenu">
                        <template v-for="(group, gi) in dropmenu" :key="`group-${gi}`">
                            <p class="submenu-group-label">{{ group.label }}</p>
                            <button v-for="(item, ri) in group.submenu" :key="`setting-${ri}`"
                                    @click="goToOption(null, item.route)"
                                    class="sub-btn"
                                    :class="{ 'sub-btn--active': current_router_name === item.route }"
                                    type="button">
                                {{ item.label }}
                            </button>
                        </template>
                    </div>
                </transition>
            </div>

        </nav>

        <!-- Logout -->
        <button @click.stop="endsession" class="logout-btn" type="button">
            <img style="width: 22px" :src="logout" alt="logout">
            <span>Log Out</span>
        </button>

    </aside>
</template>

<script setup>
    import { computed, ref } from 'vue'
    import { inject } from 'vue'
    import { useStore } from 'vuex'
    import { useRouter } from 'vue-router'

    // Images
    const logo           = require('ASSETS/images/logo')
    const gear           = require('ASSETS/icons/sidebar/gear')
    const user           = require('ASSETS/icons/sidebar/user')
    const landscape      = require('ASSETS/icons/sidebar/landscape')
    const tachometer     = require('ASSETS/icons/sidebar/tachometer')
    const devices        = require('ASSETS/icons/sidebar/devices')
    const message_square = require('ASSETS/icons/sidebar/message_square')
    const logout         = require('ASSETS/icons/sidebar/logout')
    const chart          = require('ASSETS/icons/sidebar/chart')
    const leads          = require('ASSETS/icons/sidebar/leads')

    // Hooks
    const store   = useStore()
    const router  = useRouter()
    const $redirect = inject('$redirect')

    // Computeds
    const current_router_name = computed(() => router.currentRoute.value.name)
    const session = computed(() => store.getters.getUser)

    const menu_active = computed(() => {
        for (let item of menu.value) {
            if (item.submenu) {
                for (let sub of item.submenu) {
                    if (sub.route === current_router_name.value) return item.label
                }
            } else {
                if (item.route === current_router_name.value) return item.label
            }
        }
    })

    // Variables
    const menu = ref([
        {
            label: 'Dashboard',
            icon: tachometer,
            route: 'dashboard',
            permited_roles: ['administrator']
        },
        {
            label: 'Users',
            icon: user,
            open: false,
            permited_roles: ['administrator'],
            submenu: [
                { label: 'Users',       route: 'users',        permited_roles: ['administrator'] },
                { label: 'LocalGuides', route: 'local-guides', permited_roles: ['administrator'] },
                { label: 'Travellers',  route: 'travellers',   permited_roles: ['administrator'] },
                { label: 'Partners',    route: 'partners',     permited_roles: ['administrator'] }
            ]
        },
        {
            label: 'Experiences',
            icon: landscape,
            open: false,
            permited_roles: ['administrator', 'partner'],
            submenu: [
                { label: 'Experiences',          route: 'experiences',           permited_roles: ['administrator', 'partner'] },
                { label: 'Categories',           route: 'experience-categories', permited_roles: ['administrator'] },
                { label: 'Experience Duration',  route: 'experience-durations',  permited_roles: ['administrator'] },
                { label: 'Best Places',  route: 'best-places',  permited_roles: ['administrator'] }

            ]
            
        },
    
        {
            label: 'Reservations',
            icon: devices,
            route: 'reservations',
            permited_roles: ['administrator']
        },
        {
            label: 'Payouts',
            icon: devices,
            route: 'payouts',
            permited_roles: ['administrator']
        },
        {
            label: 'Reports',
            icon: chart,
            open: false,
            permited_roles: ['administrator'],
            submenu: [
                { label: 'Log Users',       route: 'report-log-admin',       permited_roles: ['administrator'] },
                { label: 'Log Local Guide', route: 'report-log-local-guide', permited_roles: ['administrator'] },
                { label: 'Log Traveler',    route: 'report-log-traveller',   permited_roles: ['administrator'] },
                { label: 'Log Partner',     route: 'report-log-partner',     permited_roles: ['administrator'] },
                { label: 'Reserves',        route: 'report-experiences',     permited_roles: ['administrator'] },
                { label: 'Reviews',         route: 'report-reviews',         permited_roles: ['administrator'] },
                { label: 'Experiences',     route: 'report-catalogs',        permited_roles: ['administrator'] }
            ]
        },
        {
            label: 'Customer Leads',
            icon: leads,
            route: 'customer-leads',
            permited_roles: ['administrator']
        },
        {
            label: 'Support',
            icon: message_square,
            open: false,
            permited_roles: ['administrator'],
            submenu: [
                { label: 'Travelers',   route: 'support-chat', params: 'traveller',   permited_roles: ['administrator'] },
                { label: 'Local Guide', route: 'support-chat', params: 'local_guide', permited_roles: ['administrator'] }
            ]
        }
    ])

    const open_menu = ref(false)

    const dropmenu = ref([
        {
            label: 'Location',
            submenu: [
                { label: 'Languages', route: 'languages' },
                { label: 'Countries', route: 'countries' },
                { label: 'States',    route: 'states' },
                { label: 'Cities',    route: 'cities' }
            ]
        },
        {
            label: 'Vehicles',
            submenu: [
                { label: 'Colors',  route: 'colors' },
                { label: 'Brands',  route: 'vehicle-brands' },
                { label: 'Models',  route: 'vehicle-models' }
            ]
        },
        {
            label: 'Places',
            submenu: [
                { label: 'Amenities',   route: 'amenities' },
                { label: 'Services',    route: 'services' },
                { label: 'Highlights',  route: 'highlights' }
            ]
        },
        {
            label: 'Categories',
            submenu: [
                { label: 'Categories',  route: 'categories' },
                { label: 'Itinerary Categories',  route: 'itinerary-categories' },
            ]
        },
        {
            label: 'Preferences',
            submenu: [
                { label: 'Preferences', route: 'preferences' }
            ]
        },
        {
            label: 'Partners',
            submenu: [
                { label: "Partner's Categories", route: 'partner-categories' }
            ]
        },
        {
            label: 'Other',
            submenu: [
                { label: 'Skills',       route: 'skills' },
                { label: 'FAQ',          route: 'faqs' },
                { label: 'FAQ Sections', route: 'faq-sections' },
                { label: 'Banks',        route: 'banks' },
                { label: 'Documents',    route: 'documents' },
                { label: 'Currencies',   route: 'currencies' },
                 { label: 'Platform Fee', route: 'commission' },
            ]
        },
       

    ])

    // Methods
    const toggleMenu = (index) => {
        const isOpen = menu.value[index].open
        menu.value.forEach((item, i) => { item.open = false })
        if (!isOpen) menu.value[index].open = true
    }

    const goToOption = (index, route) => {
        store.commit('setSearch', '')
        open_menu.value = false
        menu.value.forEach(item => { item.open = false })
        $redirect(route)
    }

    const endsession = async () => {
        await sessionStorage.removeItem('farrther@session')
        $redirect('login')
    }
</script>

<style lang="scss" scoped>
@use 'sass:color';
@import 'ASSETS/scss/minxin.scss';

#Sidebar {
    position: fixed;
    width: 240px;
    height: 100vh;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;
    }
}

// Logo
.sidebar-logo {
    padding: 22px 20px 18px;
    cursor: pointer;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 8px;
    flex-shrink: 0;

    img { max-width: 100px; }
}

// Nav wrapper
.sidebar-nav {
    flex: 1;
    padding: 4px 10px;
}

// Divider
.sidebar-divider {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    margin: 6px 0;
}

// Main nav button
.nav-btn {
    width: 100%;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.72);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-family: 'Poppins-SemiBold', sans-serif;
    transition: background 0.2s, color 0.2s;
    text-align: left;

    @include downPixels(1440px) { font-size: 13px; }

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: white;
    }

    &--active {
        background: rgba(255, 255, 255, 0.13);
        color: white;
    }

    &--open {
        background: rgba(255, 255, 255, 0.10);
        color: white;
    }
}

.nav-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    opacity: 0.85;
}

.nav-label { flex: 1; }

.nav-chevron {
    font-size: 18px;
    line-height: 1;
    opacity: 0.5;
    transition: transform 0.25s ease;
    display: inline-block;

    &--open { transform: rotate(90deg); }
}

// Submenu container
.submenu {
    padding: 2px 0 4px 12px;
    overflow: hidden;
}

// Submenu slide animation
.submenu-enter-active {
    transition: max-height 0.28s ease, opacity 0.2s ease;
    max-height: 800px;
    opacity: 1;
}
.submenu-leave-active {
    transition: max-height 0.22s ease, opacity 0.18s ease;
    max-height: 800px;
    opacity: 1;
}
.submenu-enter-from,
.submenu-leave-to {
    max-height: 0 !important;
    opacity: 0;
}

// Settings group label
.submenu-group-label {
    font-size: 10px;
    font-family: 'Poppins-SemiBold', sans-serif;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.35);
    padding: 10px 12px 3px;
    margin: 0;
}

// Sub item button
.sub-btn {
    width: 100%;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: 'Poppins-SemiBold', sans-serif;
    text-align: left;
    transition: background 0.2s, color 0.2s;

    @include downPixels(1440px) { font-size: 12px; }

    &::before {
        content: '';
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.3);
        flex-shrink: 0;
        transition: background 0.2s;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.07);
        color: white;
    }

    &--active {
        color: white;
        background: rgba(255, 255, 255, 0.10);

        &::before { background: #4ade80; }
    }
}

// Logout
.logout-btn {
    flex-shrink: 0;
    margin: 8px 10px 20px;
    width: calc(100% - 20px);
    background: rgba(255, 255, 255, 0.06);
    border: none;
    color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-family: 'Poppins-SemiBold', sans-serif;
    transition: background 0.2s, color 0.2s;

    &:hover {
        background: rgba(255, 60, 60, 0.18);
        color: #ff6b6b;
    }
}
</style>
